import { BACKEND_API_URL, SpaqFireDb } from '@shared/core';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ICustomProperties, SeverityLevel } from '@microsoft/applicationinsights-web';
import { select, Store } from '@ngrx/store';
import { getSessionUid } from '../../selectors';
import { combineLatest } from 'rxjs';
import { APP_INSIGHTS_CONFIG, AppInsightsConfig } from './application-insights.tokens';
import { nonApiUrlPattern } from './non-api-url-pattern.util';
import { LogStoryConfig, StoryEntry, StoryType } from './story.model';

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
	private angularPlugin = new AngularPlugin();
	private appInsights = new ApplicationInsights({
		config: {
			connectionString: this.config.connectionString,
			extensions: [this.angularPlugin],
			extensionConfig: {
				[this.angularPlugin.identifier]: {
					router: this.router
				}
			},
			autoTrackPageVisitTime: true,
			disablePageUnloadEvents: ['unload'],
			disableExceptionTracking: false,
			disableAjaxTracking: false,
			excludeRequestFromAutoTrackingPatterns: [nonApiUrlPattern(this.backendApiUrl)],
			enableCorsCorrelation: true,
			correlationHeaderExcludedDomains: ['*.googleapis.com', '*.firebaseio.com', '*.firebaseapp.com'],
			enableRequestHeaderTracking: true,
			enableResponseHeaderTracking: true
		}
	});

	private enabled = this.config.enabled;
	private userId: string | null = null;

	// One rolling breadcrumb buffer per StoryType: accumulated locally via addStory() and only
	// ever sent to Application Insights when flushStory() decides it's actually needed, so
	// normal happy-path usage never pollutes App Insights with per-step traces. A story type
	// can also be disabled outright — addStory() keeps accumulating either way (it's free), but
	// flushStory() becomes a no-op, so instrumentation can stay in the code permanently and get
	// silenced per-flow without touching call sites.
	private readonly maxStoryEntries = 50;
	private stories = new Map<StoryType, StoryEntry[]>();
	// One id per active story instance — regenerated on every startStory(), so two concurrent
	// stories of the same type (e.g. the same wizard flow open in two tabs at once) are
	// distinguishable in App Insights even though their breadcrumbs overlap in time.
	private storyIds = new Map<StoryType, string>();
	private disabledStories = new Set<StoryType>();

	constructor(
		private router: Router,
		private store: Store<any>,
		private db: SpaqFireDb,
		@Inject(BACKEND_API_URL) private backendApiUrl: string,
		@Inject(APP_INSIGHTS_CONFIG) private config: AppInsightsConfig
	) {
		console.log(`[ApplicationInsights] enabled: ${this.enabled}`);
		if (!this.enabled) {
			return;
		}

		this.appInsights.loadAppInsights();
		this.appInsights.addTelemetryInitializer(envelope => {
			if (envelope.baseType === 'ExceptionData') {
				const exceptions = (envelope.baseData && envelope.baseData.exceptions) || [];
				const message = exceptions.map((e: { message: string }) => e.message).join(' ');
				if (this.isIgnoredMessage(message)) {
					return false;
				}
			}

			envelope.tags = envelope.tags || {};
			envelope.tags['ai.cloud.role'] = this.config.cloudRole;
			envelope.tags['ai.application.ver'] = this.config.environmentId;

			envelope.data = envelope.data || {};
			envelope.data['urlFirstSegment'] = this.getUrlFirstSegment();
			envelope.data['userId'] = this.userId;
			envelope.data['route'] = this.router.url;
			envelope.data['environmentId'] = this.config.environmentId;
			return true;
		});
		this.store.pipe(select(getSessionUid)).subscribe(uid => {
			this.userId = uid;
			uid ? this.setAuthenticatedUser(uid) : this.clearAuthenticatedUser();
		});
		this.patchConsoleWarn();
		this.watchLogStories();
	}

	/**
	 * Resolves RTDB's /logStories against the current uid and toggles enableStory()/disableStory()
	 * per StoryType accordingly, so a story can be turned on/off (globally or for specific users)
	 * without a deploy. A StoryType with no matching entry defaults to enabled.
	 */
	private watchLogStories(): void {
		// getSessionUid typically resolves from null to the actual uid shortly after startup, which
		// re-fires this subscribe body even though /logStories itself only changed once — so the
		// enabled-stories log is only worth printing when the resolved list actually changed.
		let previousEnabledKey: string | undefined;
		combineLatest([this.db.logStories$<LogStoryConfig[]>(), this.store.pipe(select(getSessionUid))]).subscribe(([logStories, uid]) => {
			const entries = Object.values(logStories ?? {}) as LogStoryConfig[];
			Object.values(StoryType).forEach(type => {
				const entry = entries.find(s => s?.id === type);
				const resolvedEnabled =
					!entry || (entry.enabled === true && (!entry.users?.length || (!!uid && entry.users.includes(uid))));
				resolvedEnabled ? this.enableStory(type) : this.disableStory(type);
			});
			const enabledTypes = Object.values(StoryType).filter(type => !this.disabledStories.has(type));
			const enabledKey = enabledTypes.join(',');
			if (enabledKey !== previousEnabledKey) {
				previousEnabledKey = enabledKey;
				console.log('[ApplicationInsights] enabled stories:', enabledTypes);
			}
		});
	}

	private getUrlFirstSegment(): string {
		return window.location.pathname.split('/').filter(Boolean)[0] || '';
	}

	private ignoredMessages = [
		'No Telerik and Kendo UI License found',
		'The password is invalid or the user does not have a password.',
		'Proof of ownership of a second factor is required to complete sign-in.',
		'There is no user record corresponding to this identifier. The user may have been deleted.',
		'The email address is badly formatted.',
		// Kendo PanelBar/TreeView's built-in "toggle" animation animates `display`, which Angular
		// can't interpolate — benign, comes from @progress/kendo-angular-layout's compiled code,
		// not something we can fix from app code. Just keep it out of App Insights.
		'is attempting to animate the following not animatable properties: display'
	];

	private isIgnoredMessage(message: string): boolean {
		return this.ignoredMessages.some(ignored => message.includes(ignored));
	}

	private patchConsoleWarn(): void {
		const originalWarn = console.warn.bind(console);
		console.warn = (...args: unknown[]) => {
			originalWarn(...args);
			const message = args.map(arg => (arg instanceof Error ? arg.message : String(arg))).join(' ');
			if (!this.isIgnoredMessage(message)) {
				this.trackTrace(message, SeverityLevel.Warning);
			}
		};
	}

	setAuthenticatedUser(userId: string): void {
		if (!this.enabled) {
			return;
		}
		this.appInsights.setAuthenticatedUserContext(userId, undefined, true);
	}

	clearAuthenticatedUser(): void {
		if (!this.enabled) {
			return;
		}
		this.appInsights.clearAuthenticatedUserContext();
	}

	trackEvent(name: string, properties?: ICustomProperties): void {
		if (!this.enabled) {
			return;
		}
		this.appInsights.trackEvent({ name }, properties);
	}

	trackException(error: Error, properties?: ICustomProperties): void {
		if (!this.enabled) {
			return;
		}
		this.appInsights.trackException({ exception: error, severityLevel: SeverityLevel.Error }, properties);
	}

	trackTrace(message: string, severityLevel: SeverityLevel = SeverityLevel.Information, properties?: ICustomProperties): void {
		if (!this.enabled) {
			return;
		}
		this.appInsights.trackTrace({ message, severityLevel }, properties);
	}

	/**
	 * Resets the buffer for this story type and mints a fresh instance id, discarding whatever
	 * was accumulated before. Returns the new id in case the caller wants to correlate it with
	 * something else. Not required before addStory() — it starts a story lazily if needed.
	 */
	startStory(type: StoryType): string {
		const id = this.generateStoryId();
		this.storyIds.set(type, id);
		this.stories.set(type, []);
		console.log(`[Story][${type}] started`, { id });
		return id;
	}

	/**
	 * Records a breadcrumb locally for this story type, without sending anything to
	 * Application Insights. Call this liberally along a flow you might need to reconstruct
	 * later (e.g. wizard steps, writes, payload snapshots) — it's cheap, keeps accumulating
	 * even while the story is disabled, and stays local until flushed. Lazily starts the story
	 * (minting an instance id) if nothing called startStory() yet.
	 * `payload` is for a data snapshot worth keeping (e.g. the subscriptionService object at
	 * that moment) — keep `properties` for small flat tags you'd want to filter/query on.
	 */
	addStory(type: StoryType, message: string, properties?: ICustomProperties, payload?: unknown): void {
		if (!this.storyIds.has(type)) {
			this.startStory(type);
		}
		const entries = this.stories.get(type) ?? [];
		entries.push({ timestamp: new Date().toISOString(), message, properties, payload });
		if (entries.length > this.maxStoryEntries) {
			entries.shift();
		}
		this.stories.set(type, entries);
		console.log(`[Story][${type}] added entry:`, { id: this.storyIds.get(type), message, properties, payload });
	}

	/** The current instance id for this story type, if one is active — e.g. to correlate with other telemetry. */
	getStoryId(type: StoryType): string | undefined {
		return this.storyIds.get(type);
	}

	/**
	 * Sends this story's accumulated breadcrumbs as a single trace once something actually
	 * warrants it (an unexpected/empty value, a failed validation, a suspicious state — not
	 * necessarily a thrown error), then clears the buffer. Use this at the specific checkpoints
	 * where you decide "this is worth seeing", instead of tracing every step unconditionally.
	 * If App Insights itself is disabled (e.g. local dev) or this story type is disabled, the
	 * buffer is still cleared but nothing is sent to App Insights — the story is dumped to the
	 * console instead, so the breadcrumb trail stays visible while developing/debugging locally.
	 */
	flushStory(
		type: StoryType,
		reason: string,
		severityLevel: SeverityLevel = SeverityLevel.Warning,
		properties?: ICustomProperties
	): void {
		const id = this.storyIds.get(type);
		const entries = this.stories.get(type) ?? [];
		const storyProps = this.consumeStory(type);
		const sent = this.enabled && !this.disabledStories.has(type);
		console.log(`[Story][${type}] ended (flush)`, { id, reason, entries: entries.length, sent });
		if (!sent) {
			console.log(`[Story][${type}] (console-only flush)`, { id, reason, severityLevel, properties, entries });
			return;
		}
		this.appInsights.trackTrace({ message: reason, severityLevel }, { ...properties, ...storyProps });
	}

	/** Discards this story's accumulated breadcrumbs and instance id without sending them — call when a flow completes normally. */
	clearStory(type: StoryType): void {
		const id = this.storyIds.get(type);
		const entryCount = this.stories.get(type)?.length ?? 0;
		this.stories.delete(type);
		this.storyIds.delete(type);
		console.log(`[Story] ended (clear): ${type}`, { id, entries: entryCount });
	}

	/** Silences flushStory() for this story type; addStory() keeps accumulating regardless. */
	disableStory(type: StoryType): void {
		this.disabledStories.add(type);
	}

	/** Re-enables flushStory() for this story type. */
	enableStory(type: StoryType): void {
		this.disabledStories.delete(type);
	}

	// App Insights caps a custom property value at 8192 chars and silently truncates anything
	// longer at ingestion (no client-side error, no auto-splitting) — with up to 50 entries and
	// arbitrary payload snapshots per story, JSON.stringify(entries) can blow past that easily.
	private readonly maxStoryChars = 8000;

	private consumeStory(type: StoryType): ICustomProperties {
		const entries = this.stories.get(type) ?? [];
		const storyId = this.storyIds.get(type);
		this.stories.delete(type);
		this.storyIds.delete(type);
		if (!storyId) {
			return {};
		}
		if (!entries.length) {
			return { storyType: type, storyId };
		}

		let story = this.safeStringify(entries);
		let truncated = false;
		if (story.length > this.maxStoryChars) {
			// Drop oldest entries until it fits, rather than hard-cutting the JSON string mid-way
			// (which would leave an invalid/unparseable value in the property).
			const trimmed = [...entries];
			while (trimmed.length > 1 && this.safeStringify(trimmed).length > this.maxStoryChars) {
				trimmed.shift();
			}
			story = this.safeStringify(trimmed);
			truncated = true;
		}

		return { storyType: type, storyId, story, ...(truncated ? { storyTruncated: true } : {}) };
	}

	private generateStoryId(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}

	// payload entries are arbitrary caller-supplied data (unlike the flat properties bag), so
	// stringifying them can fail on circular refs or other non-serializable values — never let
	// that take down the flush itself.
	private safeStringify(value: unknown): string {
		try {
			return JSON.stringify(value);
		} catch (error) {
			return JSON.stringify({ error: 'Unable to serialize story', message: error instanceof Error ? error.message : String(error) });
		}
	}
}
